import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { startRegistration, startAuthentication } from '@simplewebauthn/browser'
import apiInstance from '@/utils/api'

const SIMPLEWEBAUTHN_ERROR_CODES = [
  'ERROR_CEREMONY_ABORTED',
  'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED',
  'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT',
  'ERROR_AUTO_REGISTER_USER_VERIFICATION_FAILURE',
  'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT',
  'ERROR_MALFORMED_PUBKEYCREDPARAMS',
  'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG',
  'ERROR_INVALID_DOMAIN',
  'ERROR_INVALID_RP_ID',
  'ERROR_INVALID_USER_ID_LENGTH',
  'ERROR_AUTHENTICATOR_GENERAL_ERROR',
  'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY',
] as const

export function usePasskey() {
  const isLoading = ref(false)
  const error = ref('')
  const { t } = useI18n()

  /**
   * Register a new passkey (must be authenticated).
   * 1. Fetch registration options from server
   * 2. Prompt browser credential dialog
   * 3. Send attestation to server for verification
   */
  const registerPasskey = async (alias = '') => {
    isLoading.value = true
    error.value = ''
    try {
      const { data: optionsRes } = await apiInstance.post('/auth/passkey/register/options')
      const attResp = await startRegistration({ optionsJSON: optionsRes })
      const { data: verifyRes } = await apiInstance.post('/auth/passkey/register', {
        ...attResp,
        alias: alias || '',
      })
      return { success: true, data: verifyRes }
    } catch (err: any) {
      // User cancelled the dialog — don't show an error
      if (err.name === 'NotAllowedError') {
        return { success: false, cancelled: true }
      }

      // Map @simplewebauthn/browser error codes to translated messages
      const code = err.code as string | undefined
      if (code && SIMPLEWEBAUTHN_ERROR_CODES.includes(code as any)) {
        error.value = t(`passkey.errors.${code}`)
      } else {
        error.value = err.response?.data?.message || err.message || t('passkey.errors.registrationFailed')
      }

      return { success: false, cancelled: false }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Authenticate with a passkey (unauthenticated flow).
   * 1. Fetch authentication options from server
   * 2. Prompt browser passkey selector
   * 3. Send assertion to server — returns { token }
   */
  const loginWithPasskey = async (email?: string, lang: string = 'de') => {
    isLoading.value = true
    error.value = ''
    try {
      const { data: optionsRes } = await apiInstance.post(
        '/auth/passkey/login/options',
        email ? { email, lang } : { lang },
      )
      const authResp = await startAuthentication({ optionsJSON: optionsRes })
      const { data: verifyRes } = await apiInstance.post('/auth/passkey/login', { ...authResp, lang })
      return { success: true, token: verifyRes.data?.token as string | undefined }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        return { success: false, cancelled: true, token: undefined }
      }
      error.value = err.response?.data?.message || err.message || 'Authentication failed'
      return { success: false, cancelled: false, token: undefined }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * List all passkeys registered by the current user (authenticated).
   */
  const getCredentials = async () => {
    isLoading.value = true
    error.value = ''
    try {
      const { data } = await apiInstance.get('/auth/passkey/credentials')
      return { success: true, credentials: (data.data ?? []) as PasskeyCredential[] }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to load passkeys'
      return { success: false, credentials: [] as PasskeyCredential[] }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Delete a passkey credential by its ID (authenticated).
   */
  const deleteCredential = async (credentialId: string) => {
    isLoading.value = true
    error.value = ''
    try {
      await apiInstance.delete(`/auth/passkey/credentials/${credentialId}`)

      // Signal the browser/device to remove the passkey from its credential store.
      // Supported in Chrome 132+, Safari 18.2+, Edge 132+. Silently ignored elsewhere.
      const pkc = window.PublicKeyCredential as any
      if (typeof pkc?.signalUnknownCredential === 'function') {
        await pkc.signalUnknownCredential({
          rpId: window.location.hostname,
          credentialId,
        })
      }

      return { success: true }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete passkey'
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  /** True when the current browser supports WebAuthn / passkeys */
  const isSupported = () =>
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined'

  return {
    isLoading,
    error,
    isSupported,
    registerPasskey,
    loginWithPasskey,
    getCredentials,
    deleteCredential,
  }
}

export interface PasskeyCredential {
  id: string
  alias?: string
  device_type?: string
  transports?: string[]
  counter?: number
  is_enabled?: boolean
  disabled_at?: string | null
  created_at?: string
}
